import React from 'react';
import { Navigate, RouteObject } from 'react-router-dom';
import { LazyView } from './LazyView';
import {
  getMfExposedModule,
  getMfOutlet,
  RouteSpec,
} from '@im/ui-lib/mf-utils';
import { PermissionGate } from '../auth';

const ROUTE_SPEC_PROPS = [
  'fallback',
  'path',
  'roles',
  'to',
  'view',
  'excludeFrom',
];

function validateRouteSpec(mfName: string, routeSpec: any) {
  for (const name in routeSpec) {
    if (!ROUTE_SPEC_PROPS.includes(name)) {
      console.warn(`Invalid property '${name}' found in ${mfName} routes.`);
    }
  }
}

export async function getMfRoutes(mfName: string): Promise<RouteObject[]> {
  const routeSpecs = await getMfExposedModule<RouteSpec[]>(mfName, 'routes');
  if (!routeSpecs) {
    const outlet = await getMfOutlet(mfName);
    return outlet?.routes || [];
  }

  const routes = routeSpecs
    .map((routeSpec) => {
      validateRouteSpec(mfName, routeSpec);
      const { excludeFrom, fallback, path, roles, to, view } = routeSpec;

      const appId = process.env.APP_ID;
      if (appId && excludeFrom?.includes(appId)) {
        return null;
      }
      if (!appId) {
        console.warn('[routes] appId could not be found');
      }

      if (view) {
        if (roles) {
          return {
            element: (
              <PermissionGate
                fallback={
                  fallback ? (
                    <LazyView mfName={mfName} viewName={fallback} />
                  ) : null
                }
                roles={roles}
              >
                <LazyView mfName={mfName} viewName={view} />
              </PermissionGate>
            ),
            path,
          };
        }

        return {
          element: <LazyView mfName={mfName} viewName={view} />,
          path,
        };
      }
      if (to) {
        return {
          element: <Navigate replace to={to} />,
          path,
        };
      }

      console.error(`Invalid Route Spec in ${mfName}`, routeSpec);

      return null;
    })
    .filter(Boolean);

  return routes as RouteObject[];
}

export async function getMfsRoutes(mfNames: string[]): Promise<RouteObject[]> {
  const promises: Promise<RouteObject[] | undefined>[] = [];

  mfNames.forEach((mfName) => {
    promises.push(getMfRoutes(mfName));
  });

  const routeObjectsOrUndefinedArray = await Promise.all(promises);

  let result: RouteObject[] = [];

  routeObjectsOrUndefinedArray.forEach((routeObjects) => {
    if (routeObjects) {
      result = result.concat(routeObjects);
    }
  });

  return result;
}
